import * as XLSX from 'xlsx';

/**
 * Export users data to Excel file
 * @param {Array} users - Array of user objects
 * @param {string} filename - Name of the file (without extension)
 */
export const exportToExcel = (users, filename = 'users_export') => {
  try {
    // Define column headers
    const headers = [
      'Username',
      'Email',
      'Mobile',
      'Gender',
      'Date of Birth',
      'Country',
      'State',
      'City',
      'Postal Code',
      'Content Type',
      'Followers',
      'Following',
      'Media Count'
    ];

    // Map user data to rows
    const rows = users.map(user => [
      user.username || 'N/A',
      user.email || 'N/A',
      user.mobile || 'N/A',
      user.gender || 'N/A',
      user.dob || 'N/A',
      user.country || 'N/A',
      user.state || 'N/A',
      user.city || 'N/A',
      user.postalCode || 'N/A',
      user.contentType || 'N/A',
      user.followers || 0,
      user.following || 0,
      user.mediaCount || 0
    ]);

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

    // Set column widths
    ws['!cols'] = [
      { wch: 15 }, // Username
      { wch: 25 }, // Email
      { wch: 15 }, // Mobile
      { wch: 10 }, // Gender
      { wch: 15 }, // DOB
      { wch: 15 }, // Country
      { wch: 15 }, // State
      { wch: 15 }, // City
      { wch: 12 }, // Postal Code
      { wch: 30 }, // Content Type
      { wch: 18 }, // Followers
      { wch: 12 }, // Following
      { wch: 12 }  // Media Count
    ];

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Users');

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split('T')[0];
    const fullFilename = `${filename}_${timestamp}.xlsx`;

    // Write file
    XLSX.writeFile(wb, fullFilename);

    return { success: true, filename: fullFilename };
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    throw error;
  }
};

/**
 * Export users data to PDF file
 * @param {Array} users - Array of user objects
 * @param {string} filename - Name of the file (without extension)
 */
export const exportToPDF = async (users, filename = 'users_export') => {
  try {
    // Dynamically import jsPDF and jspdf-autotable
    const [{ default: jsPDF }, autoTable] = await Promise.all([
      import('jspdf'),
      import('jspdf-autotable')
    ]);

    const doc = new jsPDF('landscape'); // Landscape orientation for better table fit

    // Add title
    doc.setFontSize(16);
    doc.text('Users Export', 14, 15);

    // Add export date
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Exported on: ${new Date().toLocaleDateString()}`, 14, 22);

    // Prepare table data
    const tableData = users.map(user => [
      user.username || 'N/A',
      user.email || 'N/A',
      user.mobile || 'N/A',
      user.gender || 'N/A',
      user.dob || 'N/A',
      user.country || 'N/A',
      user.state || 'N/A',
      user.city || 'N/A',
      user.postalCode || 'N/A',
      user.contentType || 'N/A',
      (user.followers || 0).toString(),
      (user.following || 0).toString(),
      (user.mediaCount || 0).toString()
    ]);

    // Column headers
    const headers = [
      ['Username', 'Email', 'Mobile', 'Gender', 'Date of Birth', 'Country', 'State', 'City', 'Postal Code', 'Content Type', 'Followers', 'Following', 'Media Count']
    ];

    // Add table
    doc.autoTable({
      head: headers,
      body: tableData,
      startY: 28,
      styles: {
        fontSize: 7,
        cellPadding: 2,
        overflow: 'linebreak',
        cellWidth: 'wrap'
      },
      headStyles: {
        fillColor: [255, 140, 0], // Orange color matching theme
        textColor: [255, 255, 255],
        fontStyle: 'bold'
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      margin: { top: 28, left: 14, right: 14 },
      columnStyles: {
        0: { cellWidth: 20 }, // Username
        1: { cellWidth: 30 }, // Email
        2: { cellWidth: 20 }, // Mobile
        3: { cellWidth: 15 }, // Gender
        4: { cellWidth: 20 }, // DOB
        5: { cellWidth: 20 }, // Country
        6: { cellWidth: 20 }, // State
        7: { cellWidth: 20 }, // City
        8: { cellWidth: 18 }, // Postal Code
        9: { cellWidth: 35 }, // Content Type
        10: { cellWidth: 25 }, // Followers
        11: { cellWidth: 18 }, // Following
        12: { cellWidth: 18 }  // Media Count
      }
    });

    // Add footer with page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.getWidth() / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split('T')[0];
    const fullFilename = `${filename}_${timestamp}.pdf`;

    // Save PDF
    doc.save(fullFilename);

    return { success: true, filename: fullFilename };
  } catch (error) {
    console.error('Error exporting to PDF:', error);
    // If jsPDF is not installed, show helpful error
    if (error.message && error.message.includes('Cannot find module')) {
      throw new Error('PDF export requires jspdf and jspdf-autotable packages. Please install them: npm install jspdf jspdf-autotable');
    }
    throw error;
  }
};
